import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Plus, Undo2, Pencil, Trash2 } from "lucide-react";
import type { Client, Product, ReturnWithDetails } from "@shared/schema";

const unitOptions = [
  { value: "st", label: "Styck (st)" },
  { value: "påse", label: "Påse" },
  { value: "låda", label: "Låda" },
  { value: "kg", label: "Kilogram (kg)" },
];

const unitLabels: Record<string, string> = {
  st: "st",
  "påse": "påse",
  "låda": "låda",
  kg: "kg",
};

const returnFormSchema = z.object({
  clientId: z.string().min(1, "Välj en kund"),
  productId: z.string().min(1, "Välj en produkt"),
  quantity: z.coerce.number().min(1, "Antal måste vara minst 1"),
  unit: z.string().min(1, "Välj enhet"),
  reason: z.string().optional(),
  returnDate: z.string().min(1, "Ange datum"),
  registeredBy: z.string().optional(),
});

type ReturnFormData = z.infer<typeof returnFormSchema>;

export default function AdminReturns() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReturn, setEditingReturn] = useState<ReturnWithDetails | null>(null);
  const [deletingReturnId, setDeletingReturnId] = useState<string | null>(null);

  const { data: returns, isLoading } = useQuery<ReturnWithDetails[]>({
    queryKey: ["/api/admin/returns"],
  });

  const { data: clients } = useQuery<Client[]>({
    queryKey: ["/api/admin/clients"],
  });

  const { data: products } = useQuery<Product[]>({
    queryKey: ["/api/products"],
  });

  const todayStr = new Date().toISOString().slice(0, 10);

  const form = useForm<ReturnFormData>({
    resolver: zodResolver(returnFormSchema),
    defaultValues: {
      clientId: "",
      productId: "",
      quantity: 1,
      unit: "st",
      reason: "",
      returnDate: todayStr,
      registeredBy: "",
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: ReturnFormData) =>
      apiRequest("POST", "/api/admin/returns", data),
    onSuccess: () => {
      toast({ title: "Retur registrerad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/returns"] });
      setDialogOpen(false);
      form.reset();
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte registrera retur.", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: ReturnFormData & { id: string }) =>
      apiRequest("PATCH", `/api/admin/returns/${data.id}`, data),
    onSuccess: () => {
      toast({ title: "Retur uppdaterad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/returns"] });
      setEditingReturn(null);
      form.reset();
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte uppdatera retur.", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/api/admin/returns/${id}`),
    onSuccess: () => {
      toast({ title: "Retur raderad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/returns"] });
      setDeletingReturnId(null);
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte radera retur.", variant: "destructive" });
    },
  });

  const openCreate = () => {
    form.reset({ clientId: "", productId: "", quantity: 1, unit: "st", reason: "", returnDate: todayStr, registeredBy: "" });
    setEditingReturn(null);
    setDialogOpen(true);
  };

  const openEdit = (ret: ReturnWithDetails) => {
    setEditingReturn(ret);
    const retDate = ret.returnDate
      ? new Date(ret.returnDate).toISOString().slice(0, 10)
      : ret.createdAt
        ? new Date(ret.createdAt).toISOString().slice(0, 10)
        : todayStr;
    form.reset({
      clientId: ret.clientId,
      productId: ret.productId,
      quantity: ret.quantity,
      unit: (ret as any).unit || "st",
      reason: ret.reason || "",
      returnDate: retDate,
      registeredBy: (ret as any).registeredBy || "",
    });
    setDialogOpen(true);
  };

  const onSubmit = (data: ReturnFormData) => {
    if (editingReturn) {
      updateMutation.mutate({ ...data, id: editingReturn.id });
    } else {
      createMutation.mutate(data);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Returer</h1>
        <p className="text-muted-foreground">Hantera produktreturer</p>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div>
            <CardTitle>Returlista</CardTitle>
            <CardDescription>Alla registrerade returer</CardDescription>
          </div>
          <Button onClick={openCreate} data-testid="button-add-return">
            <Plus className="h-4 w-4 mr-2" />
            Ny retur
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : returns && returns.length > 0 ? (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Datum</TableHead>
                  <TableHead>Kund</TableHead>
                  <TableHead>Produkt</TableHead>
                  <TableHead>Antal</TableHead>
                  <TableHead>Enhet</TableHead>
                  <TableHead>Registrerad av</TableHead>
                  <TableHead>Anledning</TableHead>
                  <TableHead className="text-right">Åtgärd</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {returns.map((ret) => (
                  <TableRow key={ret.id}>
                    <TableCell>
                      {(ret as any).returnDate
                        ? new Date((ret as any).returnDate).toLocaleDateString("sv-SE")
                        : ret.createdAt
                          ? new Date(ret.createdAt).toLocaleDateString("sv-SE")
                          : "-"}
                    </TableCell>
                    <TableCell className="font-medium">{ret.client?.name || "-"}</TableCell>
                    <TableCell>{ret.product?.name || "-"}</TableCell>
                    <TableCell>{ret.quantity}</TableCell>
                    <TableCell>{unitLabels[(ret as any).unit] || (ret as any).unit || "st"}</TableCell>
                    <TableCell>{(ret as any).registeredBy || "-"}</TableCell>
                    <TableCell>{ret.reason || "-"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openEdit(ret)}
                          data-testid={`button-edit-return-${ret.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <AlertDialog open={deletingReturnId === ret.id} onOpenChange={(open) => !open && setDeletingReturnId(null)}>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="text-destructive"
                              onClick={() => setDeletingReturnId(ret.id)}
                              data-testid={`button-delete-return-${ret.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Radera retur?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Vill du radera returen för {ret.product?.name} från {ret.client?.name}? Denna åtgärd kan inte ångras.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Avbryt</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteMutation.mutate(ret.id)}
                                disabled={deleteMutation.isPending}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                {deleteMutation.isPending ? "Raderar..." : "Radera"}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          ) : (
            <div className="py-12 text-center">
              <Undo2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Inga returer registrerade</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) { setDialogOpen(false); setEditingReturn(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingReturn ? "Redigera retur" : "Registrera retur"}</DialogTitle>
            <DialogDescription>{editingReturn ? "Ändra informationen om returen" : "Ange information om returen"}</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="clientId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Kund</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger data-testid="select-return-client">
                          <SelectValue placeholder="Välj kund" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {clients?.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="productId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Produkt</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger data-testid="select-return-product">
                          <SelectValue placeholder="Välj produkt" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {products?.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="quantity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Antal</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" min={1} data-testid="input-return-quantity" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="unit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Enhet</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger data-testid="select-return-unit">
                            <SelectValue placeholder="Välj enhet" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {unitOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="returnDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Returdatum</FormLabel>
                    <FormControl>
                      <Input {...field} type="date" data-testid="input-return-date" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="registeredBy"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Registrerad av</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Namn på den som registrerar" data-testid="input-return-registered-by" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Anledning</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Beskriv anledningen till returen" data-testid="input-return-reason" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setEditingReturn(null); }}>
                  Avbryt
                </Button>
                <Button type="submit" disabled={isSaving} data-testid="button-save-return">
                  {isSaving ? "Sparar..." : editingReturn ? "Spara" : "Registrera"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
